import type { Faq, Review } from '@/types'

export const topGun = {
    name: "Top Gun Concrete Lifting",
    region: "Kansas & Missouri",
    phone: "877-9-TOPGUN",
    phoneHref: "tel:+18779867486",
    localPhone: "(785) 248-6990",
    localPhoneHref: "tel:+17852486990",
    email: "info@topgunconcretelifting.com",
    emailHref: "mailto:info@topgunconcretelifting.com",
    estimatorHref: "https://www.topgunconcretelifting.com/estimator",
    galleryHref: "https://www.topgunconcretelifting.com/gallery-1",
    aboutHref: "https://www.topgunconcretelifting.com/about",
    reviewsHref: "https://www.google.com/search?q=Top+Gun+Concrete+Lifting&ludocid=4077124593667806542#lrd=0x4182ced5c818db7d:0x3894db376694154e,1,,,,",
    rating: 5.0,
    reviewCount: 38,
    guaranteeYears: 10
}

export const topGunMarquee = [
    "Driveways",
    "Sidewalks",
    "Patios & pool decks",
    "Steps & porches",
    "Concrete lifting"
]

export interface TopGunIssue {
    id: string
    title: string
    description: string
}

export const topGunIssues: TopGunIssue[] = [
    { id: '01', title: "A drop at the garage", description: "A driveway slab has settled below the garage floor." },
    { id: '02', title: "An uneven sidewalk", description: "A raised edge or a low section along the path." },
    { id: '03', title: "Sinking steps or a porch", description: "A gap at the landing, steps or front entry." },
    { id: '04', title: "A settled patio or pool deck", description: "An outdoor slab sits lower than its neighbors." },
    { id: '05', title: "Water collecting near the house", description: "A low spot or runoff heading the wrong way." },
    { id: '06', title: "Not sure what needs attention", description: "You know something has changed. We can help." }
]

export interface TopGunService {
    number: string
    kicker: string
    title: string
    description: string
    image: string
    imageAlt: string
}

export const topGunServices: TopGunService[] = [
    {
        number: '01',
        kicker: "At the garage",
        title: "Driveways & garage approaches",
        description: "Address the drop where a settled driveway meets the garage. Keep the concrete that can be repaired.",
        image: '/images/topgun/garage-approach.webp',
        imageAlt: "Before and after photos of a garage approach lifted by Top Gun"
    },
    {
        number: '02',
        kicker: "On the way home",
        title: "Sidewalks & walkways",
        description: "Bring sunken sections back into place along the paths you use every day.",
        image: '/images/topgun/walkway.webp',
        imageAlt: "Residential walkway after Top Gun concrete lifting"
    },
    {
        number: '03',
        kicker: "A better welcome",
        title: "Steps, stoops & porches",
        description: "Assess the settled landing, steps or front porch at the entrance to your home.",
        image: '/images/topgun/entry-steps.webp',
        imageAlt: "Front entry steps after Top Gun concrete lifting"
    },
    {
        number: '04',
        kicker: "Out in the backyard",
        title: "Patios & pool decks",
        description: "Repair settled concrete around the spaces where you gather, relax and spend time together.",
        image: '/images/topgun/pool-deck.webp',
        imageAlt: "Residential pool deck after a Top Gun concrete lifting project"
    }
]

export const topGunSteps = [
    { number: '01', title: "The void", description: "Support falls away beneath the slab, and one edge settles below where it should sit." },
    { number: '02', title: "The fill", description: "Small access holes let us inject expanding polyfoam that fills the empty space beneath your slab." },
    { number: '03', title: "The lift", description: "The foam supports the concrete as it rises. We patch the access holes and check the finished work with you." }
]

export const topGunAssessment = [
    { number: '01', title: "The concrete", description: "Which surfaces need attention, and how much has settled." },
    { number: '02', title: "The surroundings", description: "Access, adjoining slabs and where rainwater collects." },
    { number: '03', title: "The photos", description: "Clear views of the whole area and the gaps you've noticed." }
]

export const topGunTeam = [
    { name: "Brayden", role: "Operations", image: '/images/topgun/team-brayden.webp' },
    { name: "Derrick", role: "Production Manager", image: '/images/topgun/team-derrick.webp' },
    { name: "Janie", role: "Office Assistant", image: '/images/topgun/team-janie.webp' },
    { name: "Jeremy", role: "Head of Marketing & Business Development", image: '/images/topgun/team-jeremy.webp' },
    { name: "Shayleen", role: "Business Manager", image: '/images/topgun/team-shayleen.webp' }
]

export const topGunReviews: Review[] = [
    { quote: "Jeremy at Top Gun lifted our sagging concrete front porch and stairs.", name: "Donna Hultine", town: "July 24, 2025", type: "Google" },
    { quote: "Derrick was our technician assigned to the project, and did a great job while explaining each step of the process.", name: "John Guthrie", town: "October 8, 2026", type: "Google" },
    { quote: "Everyone we worked with was absolutely great.", name: "Rachelle Soden", town: "September 23, 2026", type: "Google" }
]

export const topGunAreas = {
    kansas: ["Manhattan", "Junction City", "Topeka", "Lawrence", "Ottawa", "Johnson County"],
    missouri: ["KC metro", "Lake of the Ozarks"]
}

export const topGunFaqs: Faq[] = [
    {
        question: "Can my concrete be lifted instead of replaced?",
        answer: "Sound concrete can often be lifted instead of replaced. We inspect your slab and explain the repair that fits your home."
    },
    {
        question: "What is polyfoam concrete lifting?",
        answer: "Material is injected through small access holes beneath the slab. It expands into the space below, providing support as the concrete is raised."
    },
    {
        question: "What causes concrete to sink?",
        answer: "Some clay-rich soils swell when wet and shrink as they dry. Loose fill can settle, and water can wash soil away. A slab can sink as support changes beneath it. Tell us about gaps, low spots and where rainwater collects so the assessment considers drainage as well as the lift."
    },
    {
        question: "How much will my project cost?",
        answer: "The size, access, condition, and extent of settlement vary from property to property. Use the Easy Estimator to share your project details and photos for assessment."
    },
    {
        question: "Where do you work?",
        answer: "We serve Manhattan, Junction City, Topeka, Lawrence, Ottawa, Johnson County, the KC metro, and Lake of the Ozarks. Share your project address so we can confirm coverage for your home."
    }
]
